import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, AlertCircle, TrendingUp, DollarSign, Activity, LayoutGrid, Table } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const SMALL_AMOUNT_THRESHOLD = 0.00001;

function truncateAddress(addr) {
  if (!addr || addr.length < 12) return addr;
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function formatTokenAmount(amount) {
  const n = Number(amount);
  if (n === 0) return '0';
  if (Number.isNaN(n)) return '—';
  if (n > 0 && n < SMALL_AMOUNT_THRESHOLD) return '<0.00001';
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  if (n >= 1) return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  if (n >= 0.0001) return n.toFixed(4);
  return n.toFixed(5);
}

function formatUSD(value) {
  if (!value || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function PositionCard({ position, index }) {
  // Use same formatting as table for consistency
  const minPriceStr = position.minPrice != null
    ? (position.minPrice >= 1000 ? position.minPrice.toLocaleString('en-US', { maximumFractionDigits: 2 }) : position.minPrice.toFixed(4))
    : '—';
  const maxPriceStr = position.maxPrice != null
    ? (position.maxPrice >= 1000 ? position.maxPrice.toLocaleString('en-US', { maximumFractionDigits: 2 }) : position.maxPrice.toFixed(4))
    : '—';
  const avgPriceStr = position.averagePrice != null
    ? (position.averagePrice >= 1000 ? position.averagePrice.toLocaleString('en-US', { maximumFractionDigits: 2 }) : position.averagePrice.toFixed(4))
    : '—';

  const assetSold = position.assetSold || { symbol: '', amount: 0 };
  const assetPurchased = position.assetPurchased || { symbol: '', amount: 0 };
  const assetRefunded = position.assetRefunded || { symbol: '', amount: 0 };

  const isBuy = position.orderType === 'BUY';
  const isSell = position.orderType === 'SELL';
  const isActive = position.isOpen;

  return (
    <div
      className="rounded-xl border border-[#676DFF]/20 bg-[#151515]/80 p-4 hover:border-[#676DFF]/40 transition-all"
      style={{ fontFamily: 'Lexend, sans-serif' }}
    >
      {/* Row 1: Order # + Date + Type pill */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#676DFF]">#{index != null ? index : position.positionId}</span>
          <span className="text-xs text-[#808080]">{position.createdAt || '—'}</span>
        </div>
        <span
          className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${
            isBuy ? 'bg-green-500/20 text-green-400 border border-green-500/30' : isSell ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-[#808080]/20 text-[#B8B8B8] border border-[#808080]/30'
          }`}
        >
          {position.orderType || '—'}
        </span>
      </div>

      {/* Asset (token pair) - primary identifier */}
      <div className="mb-3">
        <span className="text-[10px] text-[#808080] uppercase tracking-wide">Asset</span>
        <p className="text-base font-semibold text-white mt-0.5">{position.tokenPair || '—'}</p>
      </div>

      {/* Active: Buy/Sell Range | Closed: Avg Price */}
      <div className="rounded-lg bg-[#0A0A0A]/50 border border-[#676DFF]/15 p-3 mb-3">
        {isActive ? (
          <>
            <span className="text-[10px] text-[#808080] uppercase tracking-wide">Buy/Sell Range</span>
            <p className="text-sm font-mono text-white mt-1 font-medium">
              {minPriceStr} → {maxPriceStr}
            </p>
            <span className="text-[10px] text-[#808080] mt-0.5 block">Lower limit → Upper limit</span>
          </>
        ) : (
          <>
            <span className="text-[10px] text-[#808080] uppercase tracking-wide">Avg Price</span>
            <p className="text-sm font-mono text-white mt-1 font-medium">{avgPriceStr}</p>
          </>
        )}
      </div>

      {/* Asset Sold / Asset Purchased - same as table */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-lg bg-[#0A0A0A]/40 border border-white/5 p-2.5">
          <span className="text-[10px] text-[#808080] uppercase tracking-wide">Asset Sold</span>
          <p className="text-sm font-medium text-white mt-1 truncate">
            {assetSold.amount > 0 ? `${formatTokenAmount(assetSold.amount)} ${assetSold.symbol}` : '—'}
          </p>
        </div>
        <div className="rounded-lg bg-[#0A0A0A]/40 border border-white/5 p-2.5">
          <span className="text-[10px] text-[#808080] uppercase tracking-wide">Asset Purchased</span>
          <p className="text-sm font-medium text-white mt-1 truncate">
            {assetPurchased.amount > 0 ? `${formatTokenAmount(assetPurchased.amount)} ${assetPurchased.symbol}` : '—'}
          </p>
          {assetRefunded.amount > 0.0001 && (
            <p className="text-[10px] text-[#808080] mt-0.5">
              + {formatTokenAmount(assetRefunded.amount)} {assetRefunded.symbol} refunded
            </p>
          )}
        </div>
      </div>

      {/* Footer: position id / wallet if present */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#808080] pt-2 border-t border-white/5">
        <span className="font-mono">ID: {position.positionId}</span>
        {position.walletLabel && (
          <>
            <span>·</span>
            <span className="text-[#B8B8B8]">{position.walletLabel}</span>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryMetric({ label, value, icon: Icon, highlight }) {
  return (
    <div className="bg-[#151515]/80 border border-[#676DFF]/20 rounded-xl p-4">
      <div className="flex items-center gap-2 text-[#B8B8B8] text-xs mb-2">
        {Icon && <Icon className="h-4 w-4 text-[#676DFF]" />}
        <span className="font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${highlight ? 'text-[#676DFF]' : 'text-white'}`}>
        {value}
      </div>
    </div>
  );
}

function PositionTableRow({ position, index, hideRange = false, hideAvgPrice = false }) {
  // Format price values
  const minPriceStr = position.minPrice ? (position.minPrice >= 1000 ? position.minPrice.toLocaleString('en-US', { maximumFractionDigits: 2 }) : position.minPrice.toFixed(4)) : '—';
  const maxPriceStr = position.maxPrice ? (position.maxPrice >= 1000 ? position.maxPrice.toLocaleString('en-US', { maximumFractionDigits: 2 }) : position.maxPrice.toFixed(4)) : '—';
  const avgPriceStr = position.averagePrice ? (position.averagePrice >= 1000 ? position.averagePrice.toLocaleString('en-US', { maximumFractionDigits: 2 }) : position.averagePrice.toFixed(4)) : '—';
  
  // Asset sold, purchased, and refunded from backend
  const assetSold = position.assetSold || { symbol: '', amount: 0 };
  const assetPurchased = position.assetPurchased || { symbol: '', amount: 0 };
  const assetRefunded = position.assetRefunded || { symbol: '', amount: 0 };

  return (
    <tr className="border-b border-[#676DFF]/10 hover:bg-[#676DFF]/5 transition-colors">
      <td className="py-3 px-4 text-center">
        <div className="text-sm font-semibold text-[#676DFF]">{index}</div>
      </td>
      <td className="py-3 px-4">
        <div className="text-xs text-[#808080]">{position.createdAt || '—'}</div>
      </td>
      <td className="py-3 px-4">
        <div className="font-semibold text-white">{position.tokenPair}</div>
      </td>
      <td className="py-3 px-4">
        <span
          className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${
            position.orderType === 'BUY'
              ? 'bg-green-500/20 text-green-400'
              : position.orderType === 'SELL'
              ? 'bg-red-500/20 text-red-400'
              : 'bg-[#808080]/20 text-[#B8B8B8]'
          }`}
        >
          {position.orderType || 'Unknown'}
        </span>
      </td>
      {!hideRange && (
        <td className="py-3 px-4 text-center">
          <div className="text-sm text-white font-mono">
            {minPriceStr} → {maxPriceStr}
          </div>
        </td>
      )}
      {!hideAvgPrice && (
        <td className="py-3 px-4 text-center text-sm text-white font-mono">
          {avgPriceStr}
        </td>
      )}
      <td className="py-3 px-4 text-sm">
        {assetSold.amount > 0 ? (
          <div className="text-white font-medium">
            {formatTokenAmount(assetSold.amount)} {assetSold.symbol}
          </div>
        ) : (
          <span className="text-[#808080]">—</span>
        )}
      </td>
      <td className="py-3 px-4 text-sm">
        {assetPurchased.amount > 0 ? (
          <div>
            <div className="text-white font-medium">
              {formatTokenAmount(assetPurchased.amount)} {assetPurchased.symbol}
            </div>
            {assetRefunded.amount > 0.0001 && (
              <div className="text-[10px] text-[#808080] mt-0.5">
                + {formatTokenAmount(assetRefunded.amount)} {assetRefunded.symbol} refunded
              </div>
            )}
          </div>
        ) : (
          <span className="text-[#808080]">—</span>
        )}
      </td>
    </tr>
  );
}

export default function LPPositionsSection({ client, clientId, isAdminPreview } = {}) {
  const [activeTab, setActiveTab] = useState('open');
  const [viewMode, setViewMode] = useState('table'); // Default to table view
  const [openPositions, setOpenPositions] = useState([]);
  const [closedPositions, setClosedPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPositions = useCallback(async () => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout for LP position fetch
    try {
      const url = isAdminPreview && clientId
        ? `${API_URL}/api/admin/view-as/${clientId}/lp-positions`
        : `${API_URL}/api/client/lp-positions`;
      const token = isAdminPreview ? localStorage.getItem('adminToken') : localStorage.getItem('token');
      if (!token) {
        clearTimeout(timeoutId);
        setError('Not authenticated');
        return;
      }
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.message || data.error || (res.status === 401 ? 'Session expired' : 'Failed to load positions');
        throw new Error(msg);
      }
      // Sort positions by date (latest first)
      const sortByDate = (positions) => {
        return [...positions].sort((a, b) => {
          let dateA, dateB;
          
          // Try to use createdAtTimestamp first, otherwise parse the formatted date
          if (a.createdAtTimestamp) {
            dateA = new Date(a.createdAtTimestamp);
          } else if (a.createdAt && a.createdAt !== '—') {
            // Parse dd/mm/yy format
            const [day, month, year] = a.createdAt.split('/');
            dateA = new Date(`20${year}-${month}-${day}`);
          } else {
            dateA = new Date(0);
          }
          
          if (b.createdAtTimestamp) {
            dateB = new Date(b.createdAtTimestamp);
          } else if (b.createdAt && b.createdAt !== '—') {
            // Parse dd/mm/yy format
            const [day, month, year] = b.createdAt.split('/');
            dateB = new Date(`20${year}-${month}-${day}`);
          } else {
            dateB = new Date(0);
          }
          
          return dateB - dateA; // Descending order (newest first)
        });
      };
      
      setOpenPositions(sortByDate(data.openPositions || []));
      setClosedPositions(sortByDate(data.closedPositions || []));
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        setError('Request took too long. Please try again.');
      } else {
        setError(err.message || 'Failed to load LP positions');
      }
      setOpenPositions([]);
      setClosedPositions([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, isAdminPreview]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  const openCount = openPositions.length;
  const closedCount = closedPositions.length;
  const totalCount = openCount + closedCount;

  // Calculate total estimated value across open positions using CURRENT market price
  const totalEstimatedValue = openPositions.reduce((sum, pos) => {
    const a0 = Number(pos.token0?.amount ?? 0);
    const a1 = Number(pos.token1?.amount ?? 0);
    const currentPrice = pos.currentPrice || 
      (pos.minPrice && pos.maxPrice ? (pos.minPrice + pos.maxPrice) / 2 : 0);
    // Use ACTUAL amounts for value, not threshold-filtered
    return sum + (a0 * currentPrice + a1);
  }, 0);

  return (
    <div className="space-y-6" style={{ fontFamily: 'Lexend, sans-serif' }}>
      {/* Error Banner */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-xl border border-red-500/30 bg-red-500/10 text-red-400">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <div>
            <div className="font-semibold">Failed to load positions</div>
            <div className="text-sm text-red-300 mt-0.5">{error}</div>
          </div>
        </div>
      )}

      {/* Summary Metrics */}
      {!loading && !error && totalCount > 0 && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryMetric label="Total Orders" value={totalCount} icon={DollarSign} />
            <SummaryMetric label="Active Orders" value={openCount} icon={TrendingUp} highlight={openCount > 0} />
            <SummaryMetric label="Closed Orders" value={closedCount} icon={Activity} />
            <SummaryMetric
              label="Total Value (Active Orders)"
              value={totalEstimatedValue > 0 ? formatUSD(totalEstimatedValue) : '—'}
              icon={DollarSign}
              highlight={totalEstimatedValue > 0}
            />
          </div>
          
          {/* View Toggle - only on lg+ where table is available */}
          <div className="hidden lg:flex justify-end">
            <div className="flex border border-[#676DFF]/30 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`p-2 transition-colors ${
                  viewMode === 'table'
                    ? 'bg-[#676DFF] text-white'
                    : 'bg-transparent text-[#B8B8B8] hover:bg-[#676DFF]/10'
                }`}
                title="Table view"
              >
                <Table className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('cards')}
                className={`p-2 transition-colors ${
                  viewMode === 'cards'
                    ? 'bg-[#676DFF] text-white'
                    : 'bg-transparent text-[#B8B8B8] hover:bg-[#676DFF]/10'
                }`}
                title="Card view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-xl border border-[#676DFF]/20 bg-[#151515]/80 p-5 animate-pulse"
            >
              <div className="h-6 bg-[#333] rounded w-1/3 mb-4" />
              <div className="h-4 bg-[#333] rounded w-2/3 mb-3" />
              <div className="h-16 bg-[#333] rounded w-full mb-3" />
              <div className="h-4 bg-[#333] rounded w-full" />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex gap-2 border-b border-[#676DFF]/20">
            <button
              type="button"
              onClick={() => setActiveTab('open')}
              className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition-all ${
                activeTab === 'open'
                  ? 'bg-[#676DFF]/15 text-[#676DFF] border-t-2 border-x-2 border-[#676DFF]/40 border-b-transparent -mb-px'
                  : 'text-[#B8B8B8] hover:text-white hover:bg-[#676DFF]/5'
              }`}
            >
              Active ({openCount})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('closed')}
              className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition-all ${
                activeTab === 'closed'
                  ? 'bg-[#676DFF]/15 text-[#676DFF] border-t-2 border-x-2 border-[#676DFF]/40 border-b-transparent -mb-px'
                  : 'text-[#B8B8B8] hover:text-white hover:bg-[#676DFF]/5'
              }`}
            >
              Closed ({closedCount})
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'open' && (
            <>
              {openCount === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-4 rounded-xl border-2 border-dashed border-[#676DFF]/20 bg-[#151515]/30 text-center">
                  <div className="w-16 h-16 rounded-full bg-[#676DFF]/10 flex items-center justify-center mb-4">
                    <Wallet className="h-8 w-8 text-[#676DFF]/60" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">No active orders</h3>
                  <p className="text-sm text-[#808080] max-w-md">
                    Active orders from your tracked wallets will appear here.
                  </p>
                </div>
              ) : (
                <>
                  {/* Table: only on lg+ when table view is selected */}
                  <div className={viewMode === 'table' ? 'hidden lg:block' : 'hidden'}>
                    <div className="rounded-xl border border-[#676DFF]/20 bg-[#151515]/80 overflow-hidden">
                      <table className="w-full" style={{ fontFamily: 'Lexend, sans-serif' }}>
                        <thead className="bg-[#676DFF]/5 border-b border-[#676DFF]/20">
                          <tr>
                            <th className="py-2 px-4 text-center text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">#</th>
                            <th className="py-2 px-4 text-left text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">Date</th>
                            <th className="py-2 px-4 text-left text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">Asset</th>
                            <th className="py-2 px-4 text-left text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">Type</th>
                            <th className="py-2 px-4 text-center text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">
                              <div>Buy/Sell Range</div>
                              <div className="text-[10px] text-[#808080] normal-case font-normal mt-0.5">Lower Limit → Upper Limit</div>
                            </th>
                            <th className="py-2 px-4 text-left text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">Asset Sold</th>
                            <th className="py-2 px-4 text-left text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">Asset Purchased</th>
                          </tr>
                        </thead>
                        <tbody>
                          {openPositions.map((pos, idx) => (
                            <PositionTableRow key={pos.positionId} position={pos} index={idx + 1} hideAvgPrice />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* Cards: on mobile always; on lg+ when card view selected */}
                  <div className={viewMode === 'table' ? 'lg:hidden' : ''}>
                    <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2">
                      {openPositions.map((pos, idx) => (
                        <PositionCard key={pos.positionId} position={pos} index={idx + 1} />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {activeTab === 'closed' && (
            <>
              {closedCount === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-4 rounded-xl border-2 border-dashed border-[#505050]/20 bg-[#151515]/30 text-center">
                  <div className="w-16 h-16 rounded-full bg-[#505050]/10 flex items-center justify-center mb-4">
                    <Activity className="h-8 w-8 text-[#808080]/60" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#E0E0E0] mb-2">No closed orders</h3>
                  <p className="text-sm text-[#808080] max-w-md">
                    Previously closed orders will be shown here.
                  </p>
                </div>
              ) : (
                <>
                  {/* Table: only on lg+ when table view is selected */}
                  <div className={viewMode === 'table' ? 'hidden lg:block' : 'hidden'}>
                    <div className="rounded-xl border border-[#676DFF]/20 bg-[#151515]/80 overflow-hidden">
                      <table className="w-full" style={{ fontFamily: 'Lexend, sans-serif' }}>
                        <thead className="bg-[#676DFF]/5 border-b border-[#676DFF]/20">
                          <tr>
                            <th className="py-2 px-4 text-center text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">#</th>
                            <th className="py-2 px-4 text-left text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">Date</th>
                            <th className="py-2 px-4 text-left text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">Asset</th>
                            <th className="py-2 px-4 text-left text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">Type</th>
                            <th className="py-2 px-4 text-center text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">Avg Price</th>
                            <th className="py-2 px-4 text-left text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">Asset Sold</th>
                            <th className="py-2 px-4 text-left text-xs font-semibold text-[#B8B8B8] uppercase tracking-wide">Asset Purchased</th>
                          </tr>
                        </thead>
                        <tbody>
                          {closedPositions.map((pos, idx) => (
                            <PositionTableRow key={pos.positionId} position={pos} index={idx + 1} hideRange />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* Cards: on mobile always; on lg+ when card view selected */}
                  <div className={viewMode === 'table' ? 'lg:hidden' : ''}>
                    <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2">
                      {closedPositions.map((pos, idx) => (
                        <PositionCard key={pos.positionId} position={pos} index={idx + 1} />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
